import { type CSSProperties, useEffect, useState } from 'react';

export const readAbsolutePanelStyle = (): CSSProperties | undefined => {
  if (typeof window === 'undefined') {
    return undefined;
  }

  const viewport = window.visualViewport;

  return {
    top: viewport?.pageTop ?? window.scrollY,
    height: viewport?.height ?? window.innerHeight,
  };
};

/**
 * Keeps the absolutely positioned auth sheet aligned with the visible viewport.
 *
 * The sheet cannot be fixed because iOS WebKit can render an input caret in the
 * wrong coordinate system. Since an absolute sheet does not automatically follow
 * keyboard and browser-chrome changes, this tracks the visible viewport while open.
 *
 * `pageTop` matches the dialog's document coordinates, while `height` excludes
 * space occupied by the keyboard and browser chrome.
 */
const useAbsolutePanelViewport = (enabled: boolean): CSSProperties | undefined => {
  const [style, setStyle] = useState<CSSProperties | undefined>(() => (enabled ? readAbsolutePanelStyle() : undefined));

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const update = () =>
      setStyle((previous) => {
        const next = readAbsolutePanelStyle();
        if (!next) {
          return previous;
        }

        return previous?.top === next.top && previous?.height === next.height ? previous : next;
      });

    const viewport = window.visualViewport;
    const passive = { passive: true } as const;

    update();
    viewport?.addEventListener('resize', update);
    viewport?.addEventListener('scroll', update, passive);
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, passive);

    return () => {
      viewport?.removeEventListener('resize', update);
      viewport?.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update);
    };
  }, [enabled]);

  return style;
};

export default useAbsolutePanelViewport;
